import { useTranslations } from '@vocab/react';
import { useMemo } from 'react';

import {
  PERSONAL_DETAILS,
  PREFERRED_LOCATIONS,
  RIGHT_TO_WORK,
} from 'src/components/Router';
import type { Card } from 'src/types';
import type { Location2 } from 'src/types/graphql';

import type { RightToWork } from './NextRole/RightToWork/types';
import translations from './TopNudge/.vocab';

export type DeprecatedField = 'HOME_LOCATION' | 'PREFERRED_LOCATIONS' | 'RTW';

export const isPreferredLocationDeprecated = (
  preferredLocations: Location2[],
) => preferredLocations.every((location) => location.isDeprecated);

export const isRTWCoarse = (rightsToWork: RightToWork[]) =>
  rightsToWork.some((rtw) => rtw.isDeprecated);

export const useNudgeConfig = (deprecatedField: DeprecatedField) => {
  const { t } = useTranslations(translations);

  return useMemo(() => {
    const deprecatedFieldToConfigMap = {
      HOME_LOCATION: {
        alertMessage: t(
          'Update to a more accurate home location to receive better job recommendations and ensure employers can search for you.',
        ),
        linkTitle: t('Update home location'),
        linkTo: PERSONAL_DETAILS as Card,
        alertAutomationNudge: 'deprecated-home-location-nudge',
        alertAutomationNudgeEditLink:
          'deprecated-home-location-nudge-edit-link',
      },
      PREFERRED_LOCATIONS: {
        alertMessage: t(
          'Update to a more accurate preferred location for better job recommendations.',
        ),
        linkTitle: t('Update preferred location'),
        linkTo: PREFERRED_LOCATIONS as Card,
        alertAutomationNudge: 'deprecated-preferred-location-nudge',
        alertAutomationNudgeEditLink:
          'deprecated-preferred-location-nudge-edit-link',
      },
      RTW: {
        alertMessage: t(
          'Update your right to work to provide more accurate details about your eligibility.',
        ),
        linkTitle: t('Update right to work'),
        linkTo: RIGHT_TO_WORK as Card,
        alertAutomationNudge: 'deprecated-rtw-nudge',
        alertAutomationNudgeEditLink: 'deprecated-rtw-nudge-edit-link',
      },
    };

    return deprecatedFieldToConfigMap[deprecatedField];
  }, [deprecatedField, t]);
};
